import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Clock, ArrowRight, Users, Lock, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { formatDate, availabilityStatus } from '../../lib/utils';
import { bookingCutoffStatus, isClassStarted, isClassEnded, buildJasonWhatsappUrl } from '../../lib/bookingCutoff';
import { useLiveSessionCards, LiveSessionCard } from './LiveSessionCards';
import { FeaturedClassBanner } from './FeaturedClassBanner';
import { eventClassesAPI } from '../../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section 4 — Popular Classes This Week
 * Fetches the live class list, filters out cancelled / completed / draft / full,
 * shows the next 4-6 bookable sessions.
 * Desktop → grid, Mobile → horizontal scroll (carousel).
 * Book Now goes directly to /class/:id (existing booking page).
 */

const dateOnly = (iso) => new Date(iso + 'T00:00:00');
const NOW = new Date();

export const PopularClassesSection = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const liveSessions = useLiveSessionCards();

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/classes`)
      .then((res) => {
        if (!alive) return;
        setClasses(res.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    let alive = true;
    eventClassesAPI.list().then(({ data }) => alive && setFeatured(Array.isArray(data) ? data : [])).catch(() => {});
    return () => { alive = false; };
  }, []);

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (classes || [])
      .filter((c) => {
        if (!c.date || !c.time) return false;
        if (c.booking_mode === 'participants') return false; // shown as the featured banner
        // Hide cancelled / completed / draft / pending_confirmation
        const st = (c.status || 'published').toLowerCase();
        if (['cancelled', 'draft', 'completed', 'pending_confirmation'].includes(st)) return false;
        // Only include from today onward
        if (dateOnly(c.date) < today) return false;
        // Feb 2026 cut-off rule — once the class has started (Malaysia
        // time), hide it from Upcoming Classes entirely.
        if (isClassStarted(c)) return false;
        // Skip full classes
        const avail = availabilityStatus(c);
        if (avail?.key === 'full') return false;
        return true;
      })
      .sort((a, b) => new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time))
      .slice(0, 3);
  }, [classes]);

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="popular-classes-loading">
        <div className="text-center py-8 text-muted-foreground">Loading upcoming classes…</div>
      </section>
    );
  }

  if (upcoming.length === 0 && liveSessions.length === 0 && featured.length === 0) {
    return (
      <section
        className="max-w-7xl mx-auto px-6 py-16"
        data-testid="popular-classes-empty"
        id="popular-classes-week"
      >
        <div className="mb-8 text-center">
          <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mb-2">
            Classes Available This Week
          </div>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight">Upcoming Classes</h2>
        </div>
        <div className="rounded-xl border-2 border-dashed border-[#E5E7EB] p-10 text-center text-muted-foreground">
          <p className="mb-4">No open bookable classes this week just yet.</p>
          <Link
            to="/schedule"
            className="inline-flex items-center gap-1 text-[#7E22CE] font-black uppercase tracking-widest text-xs"
          >
            View Full Schedule <ArrowRight size={12} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      className="max-w-7xl mx-auto px-6 py-16 md:py-20"
      data-testid="popular-classes-section"
      id="popular-classes-week"
    >
      {/* Explore LGRA anchor — new stable id kept alongside the legacy one. */}
      <span id="physical-classes" aria-hidden="true" />
      <div className="mb-10 text-center">
        <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mb-2">
          Classes Available This Week
        </div>
        <h2 className="text-3xl md:text-5xl font-black tracking-tight">
          Featured Upcoming Classes &amp; Events
        </h2>
        <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
          Live availability from the LGRA schedule in Bukit Jalil, Lake Gardens KL and Alam Impian. Tap Book Now to reserve your spot.
        </p>
      </div>

      <FeaturedClassBanner events={featured} className="mb-8" />

      {/* Desktop grid + Mobile horizontal scroll */}
      <div
        className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="popular-classes-grid"
      >
        {liveSessions.map((s) => (
          <LiveSessionCard key={s.id} s={s} />
        ))}
        {upcoming.map((cls) => (
          <ClassCard key={cls.id} cls={cls} onBook={() => navigate(`/class/${cls.id}`)} />
        ))}
      </div>
      <div
        className="sm:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-6 px-6"
        data-testid="popular-classes-carousel"
      >
        {liveSessions.map((s) => (
          <div key={s.id} className="snap-start flex-shrink-0 w-[85%]">
            <LiveSessionCard s={s} />
          </div>
        ))}
        {upcoming.map((cls) => (
          <div key={cls.id} className="snap-start flex-shrink-0 w-[85%]">
            <ClassCard cls={cls} onBook={() => navigate(`/class/${cls.id}`)} />
          </div>
        ))}
      </div>

      <div className="text-center mt-8">
        <Link
          to="/schedule"
          className="inline-flex items-center gap-2 text-[#7E22CE] font-black uppercase tracking-widest text-xs hover:text-[#4C1D95]"
          data-testid="view-more-upcoming-classes"
        >
          View the full weekly schedule <ArrowRight size={12} />
        </Link>
        <span className="mx-3 text-slate-300">·</span>
        <Link to="/workshops" className="inline-flex items-center gap-2 text-slate-600 font-black uppercase tracking-widest text-xs hover:text-[#7E22CE]" data-testid="view-workshops">
          Workshops &amp; events <ArrowRight size={12} />
        </Link>
      </div>
    </section>
  );
};

export const ClassCard = ({ cls, onBook }) => {
  const spotsLeft = Math.max(0, (cls.max_students || 0) - (cls.bookings_count || 0));
  const avail = availabilityStatus(cls);
  const isFull = avail?.key === 'full';
  const isLow = spotsLeft > 0 && spotsLeft <= 3;
  const cutoff = bookingCutoffStatus(cls);
  const isCutoff = cutoff.key === 'cutoff';
  // Started classes are filtered out at the parent — but guard the card
  // anyway so a stale in-memory render doesn't briefly show a live class.
  if (cutoff.key === 'started') return null;

  return (
    <div
      className="group rounded-xl bg-white border-2 border-[#E5E7EB] hover:border-[#A855F7] hover:shadow-lg transition-all p-5 flex flex-col h-full"
      data-testid={`popular-class-${cls.id}`}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#F3E8FF] text-[#4C1D95] text-[10px] font-black uppercase tracking-widest">
          {(cls.class_type || 'adult').replace('_', ' ')}
        </div>
        {isFull ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-black uppercase tracking-widest">
            Full
          </span>
        ) : isLow ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-black uppercase tracking-widest">
            {spotsLeft} left
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-black uppercase tracking-widest">
            <Users size={10} /> {spotsLeft} spots
          </span>
        )}
      </div>

      <h3 className="text-lg font-black tracking-tight leading-tight mb-2 line-clamp-2 min-h-[3rem]">
        {cls.name}
      </h3>

      <div className="space-y-1.5 text-sm text-slate-600 mb-4 flex-1">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="flex-shrink-0 text-[#A855F7]" />
          <span>{formatDate(cls.date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={14} className="flex-shrink-0 text-[#A855F7]" />
          <span>{cls.time}</span>
        </div>
        <div className="flex items-start gap-2">
          <MapPin size={14} className="flex-shrink-0 text-[#A855F7] mt-0.5" />
          <span className="line-clamp-1">{cls.location || 'TBA'}</span>
        </div>
        {cls.coach_name && (
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-2">
            Coach {cls.coach_name.replace(/^Coach\s+/i, '')}
          </div>
        )}
      </div>

      {isCutoff ? (
        <BookingClosedActions cls={cls} testIdPrefix={`popular-class-${cls.id}`} />
      ) : isFull ? (
        <Button
          disabled
          className="w-full bg-slate-200 hover:bg-slate-200 text-slate-500 font-black uppercase tracking-widest text-xs h-10 cursor-not-allowed"
          data-testid={`popular-class-${cls.id}-full`}
        >
          Fully Booked
        </Button>
      ) : (
        <Button
          onClick={onBook}
          className="w-full bg-[#A855F7] hover:bg-[#7E22CE] text-white font-black uppercase tracking-widest text-xs h-10"
          data-testid={`popular-class-${cls.id}-book`}
        >
          Book Now <ArrowRight size={14} className="ml-1" />
        </Button>
      )}
    </div>
  );
};

/** Shared "Booking Closed / Class Ended" action block. Used on every
 *  card / booking surface once the 15-min cut-off is reached.
 *
 *    variant = "card"   — compact for grid cards (default)
 *    variant = "detail" — larger, split into (1) grey CTA button showing
 *                         status, (2) explanation copy, (3) secondary
 *                         "WhatsApp Jason" button. Used on the /class/:id
 *                         deep-link page. When the class has already ended,
 *                         only the "Class Ended" chip renders and the
 *                         WhatsApp button is hidden.
 */
export const BookingClosedActions = ({ cls, testIdPrefix = 'class', variant = 'card' }) => {
  const waUrl = buildJasonWhatsappUrl(cls.name, formatDate(cls.date), cls.time);
  const ended = isClassEnded(cls);

  if (variant === 'detail') {
    return (
      <div className="flex flex-col gap-3" data-testid={`${testIdPrefix}-cutoff-block`}>
        <Button
          disabled
          className="w-full h-11 bg-slate-200 hover:bg-slate-200 text-slate-500 font-black uppercase tracking-widest text-xs cursor-not-allowed"
          data-testid={`${testIdPrefix}-booking-closed`}
        >
          <Lock size={13} className="mr-1.5" /> {ended ? 'Class Ended' : 'Booking Closed'}
        </Button>
        {!ended && (
          <>
            <p className="text-xs text-slate-600 leading-relaxed" data-testid={`${testIdPrefix}-cutoff-note`}>
              Online booking has closed. Contact Jason to check if late entry
              is still available.
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-md border-2 border-[#25D366] bg-white hover:bg-[#25D366]/10 text-[#128C7E] font-black uppercase tracking-widest text-xs transition-colors"
              data-testid={`${testIdPrefix}-whatsapp-jason`}
            >
              <MessageSquare size={14} /> WhatsApp Jason
            </a>
          </>
        )}
      </div>
    );
  }

  // Default compact "card" variant — grey pill + underlined WhatsApp link.
  return (
    <div className="flex flex-col gap-1.5">
      <Button
        disabled
        className="w-full bg-slate-200 hover:bg-slate-200 text-slate-500 font-black uppercase tracking-widest text-xs h-10 cursor-not-allowed"
        data-testid={`${testIdPrefix}-booking-closed`}
      >
        <Lock size={12} className="mr-1" /> {ended ? 'Class Ended' : 'Booking Closed'}
      </Button>
      {!ended && (
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-[#7E22CE] font-bold underline hover:no-underline text-center leading-snug"
          data-testid={`${testIdPrefix}-whatsapp-jason`}
        >
          Want to join this class? Contact Admin Jason on WhatsApp
        </a>
      )}
    </div>
  );
};

export default PopularClassesSection;
